import { createFileRoute, Link } from '@tanstack/react-router';
import {
  ActionIcon,
  Anchor,
  Badge,
  Button,
  Card,
  Group,
  Menu,
  SegmentedControl,
  Skeleton,
  Stack,
  Table,
  Tabs,
  Text,
  Title,
} from '@mantine/core';
import {
  IconCirclePlus,
  IconCopy,
  IconDotsVertical,
  IconArchive,
  IconEdit,
  IconRefresh,
  IconRestore,
  IconTrash,
} from '@tabler/icons-react';
import { motion } from 'framer-motion';
import { useState } from 'react';
import { notifications } from '@mantine/notifications';
import { trpc } from '../../lib/trpc';
import { PermissionGate } from '../../components/PermissionGate';
import { SuppressionPanel } from '../../components/campaigns/SuppressionPanel';
import { useTabParam } from '../../lib/useTabParam';

// Phase A — full 8-value status enum, was 3 (0/1/5).
const STATUS_COLOR: Record<number, string> = {
  0: 'gray',
  1: 'blue',
  2: 'red',
  3: 'cyan',
  4: 'green',
  5: 'orange',
  6: 'yellow',
  7: 'gray',
};
const STATUS_LABEL: Record<number, string> = {
  0: 'draft',
  1: 'active',
  2: 'failed',
  3: 'processing',
  4: 'completed',
  5: 'archived',
  6: 'paused',
  7: 'cancelled',
};

function CampaignsPage() {
  const [status, setStatus] = useState<'all' | '0' | '1' | '2' | '3' | '4' | '5' | '6' | '7'>(
    'all',
  );
  const list = trpc.campaigns.list.useQuery(
    status === 'all' ? undefined : { status: Number(status) },
  );
  const utils = trpc.useUtils();
  const clone = trpc.campaigns.cloneAsDraft.useMutation({
    onSuccess: () => {
      void utils.campaigns.list.invalidate();
      notifications.show({ message: 'Cloned as draft', color: 'green' });
    },
  });
  const archive = trpc.campaigns.archive.useMutation({
    onSuccess: () => void utils.campaigns.list.invalidate(),
  });
  const unarchive = trpc.campaigns.unarchive.useMutation({
    onSuccess: () => void utils.campaigns.list.invalidate(),
  });
  const requeue = trpc.campaigns.requeueFailed.useMutation({
    onSuccess: (r) =>
      notifications.show({
        message: `Requeued ${r.updated} failed messages`,
        color: 'green',
      }),
  });
  const del = trpc.campaigns.delete.useMutation({
    onSuccess: () => void utils.campaigns.list.invalidate(),
  });

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <Stack gap="md">
        <Group justify="space-between">
          <Title order={2}>Campaigns</Title>
          <Group>
            <SegmentedControl
              value={status}
              onChange={(v) => setStatus(v as typeof status)}
              data={[
                { value: 'all', label: 'All' },
                { value: '0', label: 'Drafts' },
                { value: '1', label: 'Active' },
                { value: '3', label: 'Processing' },
                { value: '4', label: 'Completed' },
                { value: '6', label: 'Paused' },
                { value: '2', label: 'Failed' },
                { value: '7', label: 'Cancelled' },
                { value: '5', label: 'Archived' },
              ]}
            />
            <Button
              component={Link}
              to="/vendor/campaigns/new"
              leftSection={<IconCirclePlus size={14} />}
            >
              New campaign
            </Button>
          </Group>
        </Group>
        <Card p={0}>
          {list.isPending ? (
            <Skeleton height={200} m="md" />
          ) : (
            <Table striped highlightOnHover withRowBorders={false}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Title</Table.Th>
                  <Table.Th>Channel</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Scheduled</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {(list.data ?? []).map((c) => (
                  <Table.Tr key={c.id}>
                    <Table.Td>
                      <Anchor
                        renderRoot={(props) => (
                          <Link
                            to="/vendor/campaigns/$uid/status"
                            params={{ uid: c.uid }}
                            {...props}
                          />
                        )}
                      >
                        {c.title}
                      </Anchor>
                    </Table.Td>
                    <Table.Td>
                      <Badge variant="light" size="xs">
                        {c.channel === 1 ? 'WhatsApp' : 'Instagram'}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Badge color={STATUS_COLOR[c.status] ?? 'gray'} variant="light">
                        {STATUS_LABEL[c.status] ?? `status_${c.status}`}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed">
                        {c.scheduledAt ? new Date(c.scheduledAt).toLocaleString() : '—'}
                      </Text>
                    </Table.Td>
                    <Table.Td align="right">
                      <Menu shadow="md" position="bottom-end">
                        <Menu.Target>
                          <ActionIcon variant="subtle">
                            <IconDotsVertical size={14} />
                          </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                          {c.status === 0 && (
                            <Menu.Item
                              leftSection={<IconEdit size={14} />}
                              renderRoot={(props) => (
                                <Link
                                  to="/vendor/campaigns/$uid/edit"
                                  params={{ uid: c.uid }}
                                  {...props}
                                />
                              )}
                            >
                              Edit draft
                            </Menu.Item>
                          )}
                          <Menu.Item
                            leftSection={<IconCopy size={14} />}
                            onClick={() => clone.mutate({ campaignId: c.id })}
                          >
                            Clone as draft
                          </Menu.Item>
                          <Menu.Item
                            leftSection={<IconRefresh size={14} />}
                            onClick={() => requeue.mutate({ campaignId: c.id })}
                          >
                            Requeue failed
                          </Menu.Item>
                          {c.status === 5 ? (
                            <Menu.Item
                              leftSection={<IconRestore size={14} />}
                              onClick={() => unarchive.mutate({ campaignId: c.id })}
                            >
                              Unarchive
                            </Menu.Item>
                          ) : (
                            <Menu.Item
                              leftSection={<IconArchive size={14} />}
                              onClick={() => archive.mutate({ campaignId: c.id })}
                            >
                              Archive
                            </Menu.Item>
                          )}
                          <Menu.Item
                            color="red"
                            leftSection={<IconTrash size={14} />}
                            onClick={() => del.mutate({ campaignId: c.id })}
                          >
                            Delete
                          </Menu.Item>
                        </Menu.Dropdown>
                      </Menu>
                    </Table.Td>
                  </Table.Tr>
                ))}
                {(list.data ?? []).length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={5}>
                      <Text c="dimmed" ta="center" py="md">
                        No campaigns yet.
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          )}
        </Card>
      </Stack>
    </motion.div>
  );
}

const CAMPAIGN_TABS = ['campaigns', 'suppression'] as const;
type CampaignTab = (typeof CAMPAIGN_TABS)[number];

/** Campaigns hub — the campaign list plus the Do-not-contact list (formerly a
 *  separate "Suppression list" sidebar entry) as a tab. URL-driven (`?tab=`) so
 *  the old `/vendor/campaigns/suppression` link redirects straight to the tab. */
function CampaignsHub() {
  const [tab, setTab] = useTabParam<CampaignTab>('tab', CAMPAIGN_TABS, 'campaigns');
  return (
    <PermissionGate permission="manage_campaigns">
      <Tabs
        value={tab}
        onChange={(v) => setTab((v as CampaignTab) ?? 'campaigns')}
        keepMounted={false}
      >
        <Tabs.List mb="md">
          <Tabs.Tab value="campaigns">Campaigns</Tabs.Tab>
          <Tabs.Tab value="suppression">Do-not-contact</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="campaigns">
          <CampaignsPage />
        </Tabs.Panel>
        <Tabs.Panel value="suppression">
          <SuppressionPanel />
        </Tabs.Panel>
      </Tabs>
    </PermissionGate>
  );
}

export const Route = createFileRoute('/vendor/campaigns/')({
  component: CampaignsHub,
  validateSearch: (s: Record<string, unknown>): { tab?: CampaignTab } => {
    const t = s.tab;
    return typeof t === 'string' && (CAMPAIGN_TABS as readonly string[]).includes(t)
      ? { tab: t as CampaignTab }
      : {};
  },
});
